import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #121212;
  color: white;
  padding-top: 80px;
`;

const ContentContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
`;

const SpecsContainer = styled.div`
  position: relative;
  padding: 40px;
  margin: 40px 0;
  background: rgba(255, 107, 0, 0.05);
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  gap: 40px;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: #ff6b00;
  }
`;

const RightBorderContainer = styled(SpecsContainer)`
  &::before {
    left: auto;
    right: 0;
  }
`;

const SpecsTitle = styled.h2`
  font-size: 2rem;
  color: #ff6b00;
  margin-bottom: 20px;
`;

const SpecsDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: #999;
  flex: 1;
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 300px;
  background: #1a1a1a;
  border: 2px dashed #333;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px 0;
  color: #666;
  font-size: 1.1rem;
`;

const WebLine = styled(motion.div)`
  position: absolute;
  background: linear-gradient(90deg, transparent, rgba(255, 107, 0, 0.2), transparent);
  height: 1px;
  width: 100%;
`;

const WebDot = styled(motion.div)`
  position: absolute;
  width: 4px;
  height: 4px;
  background: rgba(255, 107, 0, 0.3);
  border-radius: 50%;
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 80vh;
  overflow: hidden;
  background: linear-gradient(135deg, #1a1a1a 0%, #262626 100%);
  border-radius: 12px;
  margin-bottom: 40px;
`;

const MainProductImage = styled(motion.img)`
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 10px 20px rgba(0,0,0,0.3));
  position: relative;
  z-index: 1;
`;

const SpecsImage = styled(motion.img)`
  width: 40%;
  max-height: 300px;
  object-fit: contain;
  filter: drop-shadow(0 10px 20px rgba(0,0,0,0.3));

  @media (max-width: 768px) {
    width: 100%;
  }
`;
const ButtonContainer = styled.div`
  position: absolute;
  left: 50%;
  bottom: 40px;
  transform: translateX(-50%);
  z-index: 2;
`;

const BuyNowButton = styled(Link)`
  background: #ff6b00;
  color: white;
  padding: 1rem 2rem;
  border-radius: 8px;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  font-size: 1.2rem;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(255, 107, 0, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    background: #ff8533;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(255, 107, 0, 0.4);
  }

  span {
    font-size: 1.4rem;
  }

  @media (max-width: 768px) {
    padding: 0.9rem 1.8rem;
    font-size: 1.1rem;
  }
`;

const SpecSection = styled.section`
  background: #f8f8f8;
  padding: 60px 40px;
  margin: 40px -40px;

  @media (max-width: 768px) {
    padding: 40px 20px;
    margin: 40px -20px;
  }
`;

const SpecGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  margin-bottom: 60px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SpecCard = styled.div`
  background: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  color: #333;

  h3 {
    color: #ff6b00;
    margin-bottom: 20px;
    font-size: 1.5rem;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  li {
    margin: 12px 0;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    line-height: 1.6;

    &:before {
      content: '•';
      color: #ff6b00;
      font-weight: bold;
    }
  }
`;

const DownloadSection = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 30px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const DownloadButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  background: #333;
  color: white;
  text-decoration: none;
  border-radius: 6px;
  transition: all 0.3s ease;

  &:hover {
    background: #444;
    transform: translateY(-2px);
  }

  span {
    font-size: 1.2rem;
  }
`;

const PricingSection = styled.section`
  background: linear-gradient(135deg, #1a1a1a 0%, #262626 100%);
  padding: 80px 40px;
  margin: 60px -40px;
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 107, 0, 0.3), transparent);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 107, 0, 0.3), transparent);
  }

  @media (max-width: 768px) {
    padding: 60px 20px;
    margin: 40px -20px;
  }
`;

const PriceCard = styled.div`
  max-width: 500px;
  margin: 0 auto;
  padding: 40px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const NormalPrice = styled.div`
  text-decoration: line-through;
  color: #999;
  font-size: 1.2rem;
  margin-bottom: 10px;
`;

const DiscountedPrice = styled.div`
  font-size: 3rem;
  font-weight: bold;
  color: #ff6b00;
  margin-bottom: 20px;
`;

const PreOrderButton = styled.a`
  display: inline-block;
  background: #ff6b00;
  color: white;
  padding: 15px 40px;
  border-radius: 8px;
  text-decoration: none;
  font-size: 1.2rem;
  font-weight: 500;
  margin: 20px 0;
  transition: all 0.3s ease;

  &:hover {
    background: #ff8533;
    transform: translateY(-2px);
  }
`;

const TrySection = styled.div`
  margin-top: 40px;
  text-align: left;
  
  h3 {
    color: #ff6b00;
    margin-bottom: 20px;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0 0 20px 0;
  }

  li {
    margin: 10px 0;
    display: flex;
    align-items: flex-start;
    gap: 10px;

    &:before {
      content: '•';
      color: #ff6b00;
    }
  }
`;

const TryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: white;
  color: #1a1a1a;
  padding: 12px 30px;
  border-radius: 6px;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    background: #f0f0f0;
    transform: translateY(-2px);
  }

  span {
    font-size: 1.2rem;
  }
`;

const Footnote = styled.p`
  font-size: 0.9rem;
  color: #999;
  margin-top: 10px;
  text-align: left;
`;

const ThirdPartyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ThirdPartyCard = styled.a`
  background: #1a1a1a;
  border-radius: 12px;
  padding: 25px;
  color: white;
  text-decoration: none;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 107, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 10px;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(255, 107, 0, 0.3);
    background: #222;
  }

  h4 {
    color: #ff6b00;
    margin: 0;
    font-size: 1.2rem;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
    color: #999;
  }
`;

const ThirdPartySection = styled.div`
  margin-top: 40px;

  h3 {
    color: #ff6b00;
    margin-bottom: 30px;
    text-align: center;
    font-size: 1.8rem;
  }
`;

const DirectTorque = () => {
  return (
    <PageContainer>
      <ContentContainer>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <ImageWrapper>
            {[...Array(5)].map((_, i) => (
              <WebLine
                key={`line-${i}`}
                initial={{ opacity: 0, y: i * 160 + 100 }}
                animate={{
                  opacity: [0.5, 0, 0.5],
                  x: [-20, 20, -20],
                }}
                transition={{
                  duration: 3,
                  delay: i * 0.5,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
            ))}
            {[...Array(15)].map((_, i) => (
              <WebDot
                key={`dot-${i}`}
                initial={{
                  x: Math.random() * 100 + "%",
                  y: Math.random() * 100 + "%",
                  opacity: 0
                }}
                animate={{
                  opacity: [0, 1, 0],
                  scale: [1, 1.5, 1],
                }}
                transition={{
                  duration: 2,
                  delay: i * 0.3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            ))}
            {/*
            <ButtonContainer>
              <BuyNowButton to="https://wa.me/6281916010121">
                Contact Us
                <span>↗</span>
              </BuyNowButton>
            </ButtonContainer>
            */}
            <MainProductImage 
              src="/assets/rsr-dt-x20-grass-2.png"
              alt="DirectTorque X20"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            />
          </ImageWrapper>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <SpecsContainer>
              <SpecsDescription>
                RSR DT X20 is powered by refurbished Dorna 130DNA servo motor. We have carefully curated and tested each unit to make sure it meets our quality standard. The 20 Nm peak torque is giving us a wide headroom for feedback detail. We also upgraded the encoder with a high resolution magnetic encoder (16384ppr). Whether you want the feedback detail to be smooth as butter or sharp as knife, so be it. 
              </SpecsDescription>
              <SpecsImage 
                src="/assets/servo.jpg"
                alt="Dorna Servo"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              />
            </SpecsContainer>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <RightBorderContainer>
              <SpecsImage 
                src="/assets/rsr-dtx20-pcb.png"
                alt="RSR PCB with Heart"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              />
              <SpecsDescription>
                To have full control of innovation and improvement, we designed and iterated our own PCBs that are exclusively built and engineered for RSR DT product lines. This enables us to transform the servo motor into a sleek appearance by eliminating the bulky power and encoder sockets. No more messy cables.
              </SpecsDescription>
            </RightBorderContainer>
          </motion.div>
          <SpecsContainer>
            <SpecsDescription>
            We owe a great deal to the free and open source software that helped us reach where we are today: OpenFFBoard and VESC firmware. Paired with our RSR Paddock software, we enable an easy way to control your wheelbase.
            </SpecsDescription>
            <SpecsImage 
              src="/assets/rsr-paddock.png"
              alt="RSR Paddock"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            />
          </SpecsContainer>
        </motion.div>

        <SpecSection>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <SpecGrid>
              <SpecCard>
                <h3>Product Features</h3>
                <ul>
                  <li>Peak Torque: 20 Nm</li>
                  <li>Compatible with a wide range of games</li>
                  <li>USB type C port for communication</li>
                  <li>USB Hub 4 ports for additional peripherals</li>
                  <li>4-pin Molex connector for Emergency Stop (sold separately)</li>
                  <li>RSR Paddock software for wheelbase configuration</li>
                </ul>
              </SpecCard>

              <SpecCard>
                <h3>Technical Specifications</h3>
                <ul>
                  <li>Platform: Windows PC</li>
                  <li>Drive Type: direct drive</li>
                  <li>Servo motor: Refurbished Dorna 130DNA</li>
                  <li>Base Torque: 9.55 Nm</li>
                  <li>Peak Torque: 20 Nm</li>
                  <li>Max Wheel Rotation: 1440 degree</li>
                  <li>Encoder: High resolution magnetic encoder, 16384ppr</li>
                </ul>
              </SpecCard>

              <SpecCard>
                <h3>Physical Specifications</h3>
                <ul>
                  <li>Servo Housing Material: aluminium</li>
                  <li>PCB Housing Material: 3D print ABS</li>
                  <li>Color: Black/White</li>
                  <li>Power: average 180 watt, max 360w</li>
                  <li>Power Supply Input: 110V~220V AC</li>
                  <li>Power Supply Output: 48V DC</li>
                </ul>
              </SpecCard>

              <SpecCard>
                <h3>Connectivity</h3>
                <ul>
                  <li>USB Refresh Rate: 1000Hz</li>
                  <li>USB Hub Function: supported, USB 2.0 type A</li>
                  <li>Connection Ports: DC Input, E-stop, USB, USB Hub</li>
                  <li>Power Indicator: RGB LED</li>
                  <li>Emergency Stop Switch: supported via 4-pin molex connector</li>
                </ul>
              </SpecCard>

              <SpecCard>
                <h3>Included Items</h3>
                <ul>
                  <li>Boss kit: included (70mm)</li>
                  <li>Mounting bracket: included (4mm steel plates)</li>
                  <li>User manual</li>
                  <li>Power supply</li>
                  <li>Data cable</li>
                  <li>Bracket</li>
                </ul>
              </SpecCard>

              <SpecCard>
                <h3>Downloads</h3>
                <ul>
                  <li>Documentation and Software</li>
                </ul>
                <DownloadSection>
                  <DownloadButton href="https://github.com/repas-sim-racing/rsr-dt-x20/releases">
                    User Manual
                    <span>↓</span>
                  </DownloadButton>
                  <DownloadButton href="https://github.com/repas-sim-racing/rsr-paddock/releases">
                    RSR Paddock
                    <span>↓</span>
                  </DownloadButton>
                </DownloadSection>
              </SpecCard>
            </SpecGrid>

            <ThirdPartySection>
              <h3>Powered by Open Source</h3>
              <ThirdPartyGrid>
                <ThirdPartyCard href="https://github.com/Ultrawipf/OpenFFBoard" target="_blank">
                  <h4>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.58 20 4 16.42 4 12C4 7.58 7.58 4 12 4C16.42 4 20 7.58 20 12C20 16.42 16.42 20 12 20Z" fill="#ff6b00"/>
                      <path d="M12 17L12 11" stroke="#ff6b00" strokeWidth="2" strokeLinecap="round"/>
                      <circle cx="12" cy="8" r="1" fill="#ff6b00"/>
                    </svg>
                    OpenFFBoard
                  </h4>
                  <p>Open source force feedback controller with MIT license</p>
                </ThirdPartyCard>

                <ThirdPartyCard href="https://github.com/vedderb/bldc" target="_blank">
                  <h4>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="#ff6b00" strokeWidth="2" strokeLinejoin="round"/>
                    </svg>
                    VESC Firmware
                  </h4>
                  <p>High-performance motor controller firmware (GPLv3)</p>
                </ThirdPartyCard>

                <ThirdPartyCard href="https://github.com/tauri-apps/tauri" target="_blank">
                  <h4>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="3" width="18" height="18" rx="2" stroke="#ff6b00" strokeWidth="2"/>
                      <path d="M8 8L16 16M16 8L8 16" stroke="#ff6b00" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    Tauri
                  </h4>
                  <p>Build smaller, faster, and more secure desktop apps with MIT license</p>
                </ThirdPartyCard>
              </ThirdPartyGrid>
            </ThirdPartySection>
          </motion.div>
        </SpecSection>

        <PricingSection>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <PriceCard>
              <NormalPrice>Normal price Rp14.999.000</NormalPrice>
              <DiscountedPrice>Rp7.499.000</DiscountedPrice>
              <PreOrderButton href="https://www.tokopedia.com/repas-sim-racing/rsr-directtorque-x20-20nm-direct-drive-wheelbase-for-sim-racing">
                Pre-order Now
              </PreOrderButton>
              <Footnote>* The discounted price is available for a limited number of units. Get a cheaper price by purchase directly to us (COD).</Footnote>
              <Footnote>** Usually takes 20 days</Footnote>

              <TrySection>
                <h3>Try Before You Buy</h3>
                <ul>
                  <li>Coming to our workshop</li>
                  <li>Having us come to your place*</li>
                </ul>
                <TryButton to="https://wa.me/6281916010121">
                  Yes, I want to try it
                  <span>→</span>
                </TryButton>
                <Footnote>*** Only for Jakarta Greater Area. Please note that you need to have a proper sim racing rig that could hold 15kg of mass and pay a transportation fee.</Footnote>
              </TrySection>
            </PriceCard>
          </motion.div>
        </PricingSection>
      </ContentContainer>
    </PageContainer>
  );
};

export default DirectTorque;
